import { useState, useEffect } from "react";
import {
  IonButton,
  IonCol,
  IonGrid,
  IonRow,
  IonItem,
  IonInput,
  IonLabel,
  IonTextarea,
  IonIcon,
  useIonAlert,
  IonText,
} from "@ionic/react";
import {
  mailOutline,
  notificationsOutline,
  personOutline,
  schoolOutline,
  sendOutline,
} from "ionicons/icons";
import { displayMsg, Spinner } from "pymble-ui/components";
import { useGroupNotify, GroupNotification } from "../hooks/useGroupNotify";
import Radio from "@mui/material/Radio";
import RadioGroup from "@mui/material/RadioGroup";
import FormControlLabel from "@mui/material/FormControlLabel";
import HtmlEditor from "./HtmlEditor";

import "./GroupNotify.css";

// Some warning threshholds for push notifications
const PUSH_TITLE_RECOMMENDED_CHARS = 45;
const PUSH_BODY_RECOMMENDED_CHARS = 110;
const PUSH_BODY_RECOMMENDED_LINES = 4;

interface GroupNotifyProps {
  groups: string[];
}

interface NotifyResult {
  status?: string;
  status_code?: number;
  message?: string;
  send_count?: number;
  result?: { send_count?: number };
}

const GroupNotify: React.FC<GroupNotifyProps> = ({ groups }) => {
  const [selectedMethod, setSelectedMethod] = useState("email");
  const [selectedFrom, setSelectedFrom] = useState("me");
  const [title, setTitle] = useState("");
  const [pushBody, setPushBody] = useState("");
  const [pushLines, setPushLines] = useState<number>(0);
  const [pushBodyChars, setPushBodyChars] = useState<number>(0);
  const [pushTitleChars, setPushTitleChars] = useState<number>(0);
  const [emailBody, setEmailBody] = useState("");
  const [allowSend, setAllowSend] = useState<boolean>(false);
  const [showTitleWarn, setShowTitleWarn] = useState<boolean>(false);
  const [showBodyWarn, setShowBodyWarn] = useState<boolean>(false);

  const { sendToGroups, notificationSending } = useGroupNotify();

  const handleChangeMethod = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSelectedMethod(event.target.value);
  };
  const handleChangeFrom = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSelectedFrom(event.target.value);
  };

  // Send the notification
  const handleSend = async (_event: React.MouseEvent | unknown) => {
    const notification: GroupNotification = {
      Method: selectedMethod == "push" ? "push" : "email",
      Groups: groups,
      Title: title,
      Body: selectedMethod == "push" ? pushBody : emailBody,
      From: selectedFrom == "ce" ? "ce" : "",
    };
    const result = (await sendToGroups(notification)) as NotifyResult | null;
    if (result && (result?.status == "ok" || result?.status_code)) {
      let msg = result?.message || "Notification sent";
      const count = result?.send_count ?? result?.result?.send_count;
      if (count) {
        msg += ` - [ ${count} ]`;
      }
      displayMsg(msg, { type: "success", position: "middle" });
    } else {
      displayMsg(
        `Could not send emails. ${result?.message || "Unknown error"}`,
        {
          type: "danger",
          position: "middle",
        }
      );
    }
  };

  // Calculate text and line counts
  useEffect(() => {
    if (selectedMethod == "push") {
      const returnChar = /\n/gi;
      const res = pushBody.match(returnChar);
      const lineCount = Array.isArray(res) ? res.length : 0;
      setPushLines(lineCount);
      setPushBodyChars(pushBody.length);
      setPushTitleChars(title.length);
    }
  }, [title, pushBody, selectedMethod]);

  // Check title and body limits
  useEffect(() => {
    if (selectedMethod == "push") {
      // Check title
      setShowTitleWarn(pushTitleChars > PUSH_TITLE_RECOMMENDED_CHARS);
      // Check body
      setShowBodyWarn(
        pushBodyChars > PUSH_BODY_RECOMMENDED_CHARS ||
          pushLines > PUSH_BODY_RECOMMENDED_LINES - 1
      );
    } else {
      setShowTitleWarn(false);
      setShowBodyWarn(false);
    }
  }, [selectedMethod, pushLines, pushBodyChars, pushTitleChars]);

  // Enable disable send button based on input readiness
  useEffect(() => {
    if (
      title &&
      !notificationSending &&
      ((selectedMethod == "email" && emailBody) || selectedMethod == "push")
    ) {
      setAllowSend(true);
    } else {
      setAllowSend(false);
    }
  }, [emailBody, pushBody, title, selectedMethod, notificationSending]);

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const _clearMessage = () => {
    setTitle("");
    setEmailBody("");
    setPushBody("");
  };

  // Confirmation alert
  const [confirmSend] = useIonAlert();

  const confirmSendGroup = (e: React.MouseEvent) => {
    confirmSend({
      cssClass: "group-send-alert",
      header: "Confirm",
      message: `Send this message to all members of this group?`,
      buttons: [
        "Cancel",
        {
          text: "Send to Group",
          handler: () => handleSend(e),
        },
      ],
      onDidDismiss: () => console.log("Send cancelled"),
    });
  };

  return (
    <div className="group-notify-container">
      <IonGrid className="group-notify-grid">
        {/* Method + From in a single row */}
        <IonRow className="notify-options-row">
          <IonCol size="auto" className="notify-option-col">
            <span className="notify-option-label">Method</span>
            <RadioGroup
              row
              aria-label="method"
              name="method-radio-buttons-group"
              value={selectedMethod}
              onChange={handleChangeMethod}
              className="notify-radio-group"
            >
              <FormControlLabel
                value="email"
                control={<Radio size="small" />}
                label={
                  <span className="notify-radio-label">
                    <IonIcon icon={mailOutline} />
                    Email
                  </span>
                }
              />
              <FormControlLabel
                value="push"
                control={<Radio size="small" />}
                label={
                  <span className="notify-radio-label">
                    <IonIcon icon={notificationsOutline} />
                    Push
                  </span>
                }
              />
            </RadioGroup>
          </IonCol>
          <IonCol
            size="auto"
            className={`notify-option-col${selectedMethod != "email" ? " ion-hide" : ""}`}
          >
            <span className="notify-option-label">From</span>
            <RadioGroup
              row
              aria-label="from"
              name="from-radio-buttons-group"
              value={selectedFrom}
              onChange={handleChangeFrom}
              className="notify-radio-group"
            >
              <FormControlLabel
                value="me"
                control={<Radio size="small" />}
                label={
                  <span className="notify-radio-label">
                    <IonIcon icon={personOutline} />
                    Me
                  </span>
                }
              />
              <FormControlLabel
                value="ce"
                control={<Radio size="small" />}
                label={
                  <span className="notify-radio-label">
                    <IonIcon icon={schoolOutline} />
                    Community Engagement
                  </span>
                }
              />
            </RadioGroup>
          </IonCol>
        </IonRow>

        {/* Title */}
        <IonRow>
          <IonCol>
            <IonItem color={showTitleWarn ? "warning" : ""}>
              <IonInput
                label="Title"
                labelPlacement="stacked"
                color="primary"
                value={title}
                onIonChange={(e) => setTitle(e.detail.value!)}
                placeholder="Enter a title for your message"
                type="text"
                maxlength={selectedMethod == "push" ? 65 : undefined}
              />
              <IonText
                color="danger"
                className={showTitleWarn ? "ion-padding" : "ion-hide"}
              >
                Please shorten your title to avoid truncation on receiving
                device
              </IonText>
            </IonItem>
          </IonCol>
        </IonRow>

        {/* Body */}
        <IonRow>
          <IonCol>
            <IonItem color={showBodyWarn ? "warning" : ""} lines="none">
              <IonTextarea
                label="Body"
                labelPlacement="stacked"
                color="primary"
                value={pushBody}
                onIonChange={(e) => setPushBody(e.detail.value!)}
                rows={7}
                placeholder="Enter the main body of your push notification"
                className={selectedMethod == "email" ? "ion-hide" : ""}
                maxlength={178}
              />
              <div
                className={
                  selectedMethod == "push"
                    ? "ion-hide"
                    : "notify-editor-wrapper"
                }
              >
                <IonLabel color="primary" className="notify-editor-label">
                  Body
                </IonLabel>
                <HtmlEditor
                  onChange={(value) => {
                    setEmailBody(value);
                  }}
                />
              </div>
              <IonText
                color="danger"
                className={showBodyWarn ? "ion-padding" : "ion-hide"}
              >
                Please shorten your message to avoid truncation on receiving
                device
              </IonText>
            </IonItem>
          </IonCol>
        </IonRow>

        {/* Send button */}
        <IonRow>
          <IonCol className="notify-send-col">
            {notificationSending && <Spinner />}
            <IonButton
              color="success"
              fill="solid"
              className="ion-text-capitalize"
              size="default"
              onClick={confirmSendGroup}
              disabled={!allowSend}
            >
              <IonIcon slot="start" icon={sendOutline} />
              Send Notification
            </IonButton>
          </IonCol>
        </IonRow>
      </IonGrid>
    </div>
  );
};

export default GroupNotify;
